import { Spinner, VStack } from '@chakra-ui/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';

import { useLEAccordionItemContext } from '../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import { LDFlags } from '../../../../constants/flags';
import { METHODFI_FLOW_VARIATIONS } from '../../../../constants/methodfi';
import {
  MethodFiVehicle,
  useConnectAndRetrieveEntityDataMutation,
  useCreateKyoVerificationMutation,
  useCreateMethodFiVerificationSessionMutation,
  useGetMethodFiSessionStateQuery,
} from '../../../../gql/generated/graphql';
import { useFlag, useTempInfo } from '../../../../hooks';
import AutoFillCarPanel from './AutoFillCarPanel';
import AutoFillPanel from './AutoFillPanel';
import ManualCarEntryPanel from './ManualCarEntryPanel';
import { allowOldCarAtom, methodFiConnectCalledAtom } from './MethodFiDevToolbar';
import VerifyCodePanel from './VerifyCodePanel';

type PanelMode = 'auto-fill' | 'manual' | 'code' | 'vehicle-select' | 'loading';

const isValidVehicle = (v: MethodFiVehicle | null | undefined): v is MethodFiVehicle =>
  v != null && !!v.vin;

type MethodFiEntryProps = {
  isEligible: boolean;
  onShowingManualPanel?: (showing: boolean) => void;
  onVehicleSelectMode?: (showing: boolean) => void;
};

const MethodFiEntry = ({
  isEligible,
  onShowingManualPanel,
  onVehicleSelectMode,
}: MethodFiEntryProps) => {
  const { updateState, index, setOpenAccordionIndex } = useLEAccordionItemContext();
  const allowOldCar = useAtomValue(allowOldCarAtom);
  const setConnectCalled = useSetAtom(methodFiConnectCalledAtom);
  const separateIdentityCall = useFlag(LDFlags.METHODFI_SEPARATE_IDENTITY_CALL);

  const { info, infoLoading } = useTempInfo({ fetchPolicy: 'network-only' });
  const savedVin = info?.data?.vin;
  const savedTrimName = info?.data?.kbb_trim_name ?? '';

  const isNameZipNoMethodFi =
    info?.data?.methodfi_flow_variation === METHODFI_FLOW_VARIATIONS.nameZipNoMethodFi;

  const { data: sessionStateData, loading: sessionStateLoading } = useGetMethodFiSessionStateQuery({
    fetchPolicy: 'network-only',
    skip: infoLoading || isNameZipNoMethodFi,
  });

  const [createVerificationSession] = useCreateMethodFiVerificationSessionMutation();
  const [createKyoVerification] = useCreateKyoVerificationMutation({
    context: { isErrorHandled: true },
  });
  const [connectAndRetrieveEntityData] = useConnectAndRetrieveEntityDataMutation({
    context: { isErrorHandled: true },
  });

  const [panelMode, setPanelMode] = useState<PanelMode>('loading');

  const [methodFiIds, setMethodFiIds] = useState({ entityId: '', verificationSessionId: '' });
  const [vehicles, setVehicles] = useState<MethodFiVehicle[]>([]);
  const [preselectedVin, setPreselectedVin] = useState<string | null>(null);
  const [preselectedManualCar, setPreselectedManualCar] = useState<{
    vin: string;
    trimName: string;
  }>();

  useEffect(() => {
    if (sessionStateLoading || infoLoading) return;
    if (isNameZipNoMethodFi) {
      setPanelMode('manual');
      return;
    }

    const sessionState = sessionStateData?.getMethodFiSessionState;

    if (!sessionState) {
      setPanelMode(isEligible ? 'auto-fill' : 'manual');
      return;
    }

    const { entityId, entity, vehicles: storedVehicles } = sessionState;
    const phoneVerified = entity?.verification?.phone?.verified ?? false;
    const validVehicles = (storedVehicles ?? []).filter(isValidVehicle);

    setMethodFiIds({ entityId, verificationSessionId: '' });

    if (phoneVerified && validVehicles.length > 0) {
      const matchingVehicle = savedVin && validVehicles.find((v) => v.vin === savedVin);

      setVehicles(validVehicles);
      if (matchingVehicle) {
        setPreselectedVin(savedVin);
        setPanelMode('vehicle-select');
      } else if (savedVin) {
        setPreselectedManualCar({ vin: savedVin, trimName: savedTrimName });
        setPanelMode('manual');
      } else {
        setPanelMode('vehicle-select');
      }
    } else if (phoneVerified) {
      setPanelMode('manual');
    } else {
      setPanelMode(isEligible ? 'auto-fill' : 'manual');
    }
  }, [
    sessionStateLoading,
    sessionStateData,
    isEligible,
    savedVin,
    savedTrimName,
    isNameZipNoMethodFi,
    infoLoading,
  ]);

  useEffect(() => {
    onVehicleSelectMode?.(panelMode === 'vehicle-select');
    onShowingManualPanel?.(panelMode === 'manual');
  }, [panelMode, onVehicleSelectMode, onShowingManualPanel]);

  const handleComplete = () => {
    updateState({ status: 'complete' });
    setOpenAccordionIndex(index + 1);
  };

  const handleEntityCreated = async (entityId: string) => {
    const session = await createVerificationSession({ variables: { entityId } });
    const verificationSessionId = session.data?.createVerificationSession?.id ?? '';
    setMethodFiIds({ entityId, verificationSessionId });
    setPanelMode('code');
  };

  const handleResendCode = async () => {
    const session = await createVerificationSession({
      variables: { entityId: methodFiIds.entityId },
    });
    const verificationSessionId = session.data?.createVerificationSession?.id ?? '';
    setMethodFiIds((prev) => ({ ...prev, verificationSessionId }));
    return verificationSessionId;
  };

  const handleVerificationSuccess = async () => {
    updateState({ status: 'loading' });

    try {
      await createKyoVerification({ variables: { entityId: methodFiIds.entityId } });

      setConnectCalled(true);
      const result = await connectAndRetrieveEntityData({
        variables: {
          entityId: methodFiIds.entityId,
          devOldCarOverride: allowOldCar,
          separateIdentityCall,
        },
      });
      const fetchedVehicles = (result.data?.connectAndRetrieveEntityData?.vehicles ?? []).filter(
        isValidVehicle,
      );

      updateState({ status: 'none' });
      if (fetchedVehicles.length === 0) {
        setPanelMode('manual');
      } else {
        setVehicles(fetchedVehicles);
        setPanelMode('vehicle-select');
      }
    } catch {
      updateState({ status: 'none' });
      setPanelMode('manual');
    }
  };

  if (panelMode === 'loading') {
    return (
      <VStack py={8}>
        <Spinner size="lg" color="leaseEndBlue" />
      </VStack>
    );
  }

  if (panelMode === 'auto-fill') {
    return <AutoFillPanel onSuccess={handleEntityCreated} onSkip={() => setPanelMode('manual')} />;
  }

  if (panelMode === 'code') {
    return (
      <VerifyCodePanel
        entityId={methodFiIds.entityId}
        verificationSessionId={methodFiIds.verificationSessionId}
        onEditPhoneNumber={() => setPanelMode('auto-fill')}
        onSkipVerification={() => setPanelMode('manual')}
        onVerificationSuccess={handleVerificationSuccess}
        onResendCode={handleResendCode}
      />
    );
  }

  if (panelMode === 'vehicle-select') {
    return (
      <AutoFillCarPanel
        vehicles={vehicles}
        onNotFound={() => setPanelMode('manual')}
        onComplete={handleComplete}
        preselectedVin={preselectedVin}
      />
    );
  }

  return (
    <ManualCarEntryPanel
      onReturnToVehicleList={vehicles.length > 0 ? () => setPanelMode('vehicle-select') : undefined}
      preselectedManualCar={preselectedManualCar}
    />
  );
};

export default MethodFiEntry;
